import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  ActivationRegime,
  ActivationRegimeEventDto,
  FileItem,
  InspectionCaseDto,
  InspectionIntegration,
  InspectionIntegrationDesign,
  IntegrationCommandPost
} from '@authority-labs/api-interfaces';
import { Conclusion } from '@authority-labs/enums';

@Component({
  selector: 'authority-labs-integration-test-results',
  templateUrl: './integration-test-results.component.html',
  styleUrls: ['./integration-test-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom
})
export class IntegrationTestResultsComponent implements OnInit, AfterViewInit {
  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {}

  @Input() apiUrl: string;
  @Input() inspection: InspectionIntegration;
  @Input() pageOrientation: 'portrait' | 'landscape' = 'portrait';
  @Input() pageMarginMm = 0; // each side
  @Input() maxScale = 1; // never upscale, only downscale
  @Input() minScale = 0.6; // clamp so it doesn’t become unreadable

  @ViewChild('table', { static: false })
  table!: ElementRef<HTMLDivElement>;

  Conclusion = Conclusion;
  notes: string[] = [];
  pdfUrl: string;

  get integrationDesign(): InspectionIntegrationDesign {
    return this.inspection?.integrationDesign;
  }

  get integrationCommandPost(): IntegrationCommandPost {
    return this.inspection?.integrationCommandPost;
  }

  get activationRegimes(): ActivationRegime[] {
    return this.inspection?.integrationCommandPost?.performedActions?.data;
  }

  get devicePixelRatio(): number {
    return window.devicePixelRatio;
  }

  ngOnInit(): void {
    this.notes = new InspectionIntegration(this.inspection)
      .getNotes()
      .filter((note) => note.addNotesToReport && note.notes)
      .filter((note) => !note.notes.includes('undefined'))
      .map((note) => note.notes);

    if (this.inspection?.empbededFile?.name) {
      this.getFileUrl(this.inspection.empbededFile);
    } else {
      this.pdfViewerComponentLoaded();
    }
  }

  ngAfterViewInit(): void {
    Promise.resolve().then(() => this.computeAndSetScale());
  }

  getConclusion(checkCase: InspectionCaseDto): string {
    if (checkCase.isApplicable) {
      return checkCase.isPassed ? Conclusion.Pass : Conclusion.Fail;
    }

    return Conclusion.NotApplicable;
  }

  docConclusion(b: boolean): string {
    switch (b) {
      case true:
        return Conclusion.Pass;
      case false:
        return Conclusion.Fail;
      default:
        return '--';
    }
  }

  isPassed(event: ActivationRegimeEventDto, action: string): Conclusion {
    const eventAction = event?.actions?.find((a) => a.name === action);
    switch (eventAction?.isPassed) {
      case true:
        return Conclusion.Pass;
      case false:
        return Conclusion.Fail;
      case null:
        return Conclusion.NotApplicable;
    }
  }

  getFileUrl(file: FileItem): void {
    const params = new URLSearchParams(window.location.search);
    const token = localStorage.getItem('token') ?? params.get('token');
    const tokenParam = token ? `?token=${token}` : '';

    this.http
      .get<{ downloadUrl: string }>(
        `${this.apiUrl}/files/download/${file.id}${tokenParam}`
      )
      .subscribe((data) => {
        this.pdfUrl = data.downloadUrl;
        this.cdr.markForCheck();
      });
  }

  pdfViewerComponentLoaded(): void {
    (window as any).pageLoaded = true;
  }

  private computeAndSetScale(): void {
    const el = this.table?.nativeElement;

    if (!el) {
      return;
    }

    // Natural (unconstrained) content width in px
    // Use scrollWidth to capture full width even if container is narrower
    const contentWidthPx = el.scrollWidth || 1;

    // A4 printable width (in px) based on margins & orientation
    const pageWidthMm = this.pageOrientation === 'portrait' ? 200 : 297;
    const printableWidthMm = pageWidthMm - 2 * this.pageMarginMm;
    const printableWidthPx = this.mmToPx(printableWidthMm);

    let scale = printableWidthPx / contentWidthPx;
    scale = Math.min(this.maxScale, Math.max(this.minScale, scale));

    // Expose to CSS via custom properties
    el.style.setProperty('--print-scale', String(scale));
  }

  private mmToPx(mm: number): number {
    // 1in = 25.4mm, CSS px ~ 96dpi
    return (mm / 25.4) * 96;
  }
}
